import type { OrderStatus } from '@/services/ordersApi'
import type { VerticalSlug, VerticalUiProfile } from './types'

/**
 * Flujo de estados que ve el equipo en /app/orders. El vertical decide el layout y el
 * siguiente paso; ningún nodo del backend depende de esta configuración.
 */

export interface OrderPrimaryAction {
  label: string
  next: OrderStatus
}

export interface OrderKanbanColumn {
  id: 'new' | 'kitchen' | 'ready' | 'delivered'
  label: string
  statuses: readonly OrderStatus[]
  /** Acción primaria única de la columna. */
  primaryAction?: OrderPrimaryAction
  /** La columna de entregadas muestra solo las de hoy. */
  completedTodayOnly?: boolean
}

export interface VerticalOrdersConfig {
  layout: 'kanban' | 'table'
  columns: readonly OrderKanbanColumn[]
  primaryActionByStatus: Partial<Record<OrderStatus, OrderPrimaryAction>>
  waitWarnMinutes: number
  waitCriticalMinutes: number
}

export const TABLE_ORDERS_CONFIG: VerticalOrdersConfig = {
  layout: 'table',
  columns: [],
  primaryActionByStatus: {
    draft: { label: 'Confirmar', next: 'confirmed' },
    pending_confirmation: { label: 'Confirmar', next: 'confirmed' },
    confirmed: { label: 'Iniciar despacho', next: 'in_progress' },
    in_progress: { label: 'Completar', next: 'completed' },
    ready: { label: 'Entregar', next: 'completed' },
  },
  waitWarnMinutes: 30,
  waitCriticalMinutes: 60,
}

export const KANBAN_ORDERS_CONFIG: VerticalOrdersConfig = {
  layout: 'kanban',
  columns: [
    {
      id: 'new',
      label: 'Nueva',
      statuses: ['draft', 'pending_confirmation'],
      primaryAction: { label: 'Aceptar', next: 'confirmed' },
    },
    {
      id: 'kitchen',
      label: 'En cocina',
      statuses: ['confirmed', 'in_progress'],
      primaryAction: { label: 'Marcar lista', next: 'ready' },
    },
    {
      id: 'ready',
      label: 'Lista',
      statuses: ['ready'],
      primaryAction: { label: 'Entregar', next: 'completed' },
    },
    {
      id: 'delivered',
      label: 'Entregada',
      statuses: ['completed'],
      completedTodayOnly: true,
    },
  ],
  primaryActionByStatus: {
    draft: { label: 'Aceptar', next: 'confirmed' },
    pending_confirmation: { label: 'Aceptar', next: 'confirmed' },
    confirmed: { label: 'Marcar lista', next: 'ready' },
    in_progress: { label: 'Marcar lista', next: 'ready' },
    ready: { label: 'Entregar', next: 'completed' },
  },
  waitWarnMinutes: 15,
  waitCriticalMinutes: 30,
}

const KANBAN_VERTICALS: ReadonlySet<VerticalSlug> = new Set(['restaurante', 'bodegon_licoreria'])

/**
 * Preferencia por usuario (no por negocio) y tolerante a storage bloqueado.
 * Por defecto: tablero en pantallas grandes, lista en móvil.
 */
export type OrdersViewMode = 'board' | 'list'

export function ordersViewStorageKey(tenantId: string | null, slug: VerticalSlug): string {
  return `orders-view:${tenantId ?? 'no-tenant'}:${slug}`
}

export function readOrdersViewMode(key: string): OrdersViewMode | null {
  try {
    const value = window.localStorage.getItem(key)
    return value === 'board' || value === 'list' ? value : null
  } catch {
    return null
  }
}

export function writeOrdersViewMode(key: string, mode: OrdersViewMode): void {
  try {
    window.localStorage.setItem(key, mode)
  } catch {
    // Sin storage el selector sigue funcionando en memoria.
  }
}

export function defaultOrdersViewMode(viewportWidth: number): OrdersViewMode {
  return viewportWidth >= 1024 ? 'board' : 'list'
}

export function resolveVerticalOrdersConfig(profile: Pick<VerticalUiProfile, 'slug'>): VerticalOrdersConfig {
  return KANBAN_VERTICALS.has(profile.slug) ? KANBAN_ORDERS_CONFIG : TABLE_ORDERS_CONFIG
}

export function getOrderPrimaryAction(
  status: OrderStatus,
  config: VerticalOrdersConfig
): OrderPrimaryAction | null {
  return config.primaryActionByStatus[status] ?? null
}
